import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { IAccessRequestUpdate } from 'interfaces/AccessRequest/IAccessRequestUpdate';
import { IHelperOnlineAccess } from 'interfaces/ElementModels/IHelperOnlineAccess';
import { IAccessRequest } from 'interfaces/AccessRequest/IAccessRequest';
import { PricelistApis } from 'hooks/apis/PricelistApis';

const ClientManagement = ({ client }) => {
  const { getClientAccess, postUpdateAccess, postSetPricelistAccess } = PricelistApis();
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState<string>(null);
  const [clientAccess, setClientAccess] = useState<IHelperOnlineAccess[]>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const newRequests = useMemo(
    () => clientAccess?.filter((f) => f.RequestedAccess),
    [clientAccess]
  );
  const activeUsers = useMemo(
    () => clientAccess?.filter((f) => f.HasAccess || f.IsManager),
    [clientAccess]
  );

  const setErrorSnackbar = useCallback(
    (msg) => {
      enqueueSnackbar && enqueueSnackbar(msg || 'error', { variant: 'error' });
    },
    [enqueueSnackbar]
  );

  const setSuccessSnackbar = useCallback(
    (msg) => {
      enqueueSnackbar && enqueueSnackbar(msg || 'success', { variant: 'success' });
    },
    [enqueueSnackbar]
  );

  const collectClientUsers = useCallback(() => {
    getClientAccess(client).then((response) => {
      if (response && !response.errorMessage) {
        setClientAccess(response);
      } else {
        setError(response?.errorMessage);
      }
    });
  }, [client, getClientAccess]);

  const setAccess = useCallback(
    (id, accessType, token) => {
      if (!id || !token || !accessType) {
        setErrorSnackbar(`Request has already been handled`);
      }

      const data: IAccessRequest = {
        Id: parseInt(id),
        Token: token,
        AccessType: parseInt(accessType),
      };

      postSetPricelistAccess(data).then((response) => {
        if (!response.errorMessage) {
          setSuccessSnackbar(`Access rights updated`);
          collectClientUsers();
        } else {
          setErrorSnackbar(response.errorMessage);
        }
      });
    },
    [postSetPricelistAccess, collectClientUsers, setErrorSnackbar, setSuccessSnackbar]
  );

  const updateAccess = useCallback(
    (id, accessType) => {
      if (!id || typeof accessType != "number") {
        return;
      }

      const data: IAccessRequestUpdate = {
        Id: parseInt(id),
        AccessType: accessType,
      };

      postUpdateAccess(data).then((response) => {
        if (response?.message) {
          setSuccessSnackbar(`Access rights updated`);
          collectClientUsers();
        } else if (response?.errorMessage) {
          setErrorSnackbar(response.errorMessage);
        }
      });
    },
    [postUpdateAccess, collectClientUsers, setErrorSnackbar, setSuccessSnackbar]
  );

  useEffect(() => {
    if (dialogOpen) {
      collectClientUsers();
    }
  }, [dialogOpen, collectClientUsers]);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  return (
    <Box>
      <Button onClick={() => setDialogOpen(true)}>Manage {client}</Button>

      <Dialog open={dialogOpen} maxWidth="lg" onClose={() => closeDialog()}>
        <DialogTitle>Manage Pricelist access</DialogTitle>
        <DialogContent>
          {error !== null ? <p className="errorMessage">{error}</p> : null}
          {newRequests && newRequests?.length > 0 ? <Box className="clientManagement">
            <h2>New Requests</h2>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell align="center">Grant User Access</TableCell>
                    <TableCell align="center">Grant Manager Access</TableCell>
                    <TableCell align="center">Decline Access</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {newRequests?.map((m) => (
                      <TableRow key={m.Email}>
                        <TableCell>{m.Email}</TableCell>
                        <TableCell align="center">
                          <Button onClick={() => setAccess(m.Id, 1, m.ConfirmationToken)}>o</Button>
                        </TableCell>
                        <TableCell align="center">
                          <Button onClick={() => setAccess(m.Id, 2, m.ConfirmationToken)}>o</Button>
                        </TableCell>
                        <TableCell align="center">
                          <Button onClick={() => setAccess(m.Id, 0, m.ConfirmationToken)}>o</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box> : null}

          {activeUsers && activeUsers?.length > 0 ? <Box className="clientManagement">
            <h2>Active Users</h2>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell align="center">User</TableCell>
                    <TableCell align="center">Manager</TableCell>
                    <TableCell align="center">Remove Access</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeUsers?.map((m) => (
                      <TableRow key={m.Email}>
                        <TableCell>{m.Email}</TableCell>
                        <TableCell align="center">
                          {!m.IsManager ? 'X' : <Button onClick={() => updateAccess(m.Id, 1)}>o</Button>}
                        </TableCell>
                        <TableCell align="center">{m.IsManager ? 'X' : <Button onClick={() => updateAccess(m.Id, 2)}>o</Button>}</TableCell>
                        <TableCell align="center">
                          <Button onClick={() => updateAccess(m.Id, 0)}>o</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>: null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ClientManagement;
